<template>
  <div
    class="enhanced-image-uploader mb-4 flex-shrink-1"
  >
    <v-card
      class="upload-area"
      :class="{ 'has-image': !!previewUrl }"
      outlined
      @click="triggerFileInput"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div
        v-if="!previewUrl"
        class="upload-placeholder"
      >
        <v-icon size="48">
          mdi-cloud-upload
        </v-icon>
        <div class="text-subtitle-1 mt-2">
          {{ $t('Click or drag image to upload') }}
        </div>
        <div class="text-caption">
          {{ hint }}
        </div>
      </div>

      <div
        v-else
        class="image-preview-container"
      >
        <img
          :src="previewUrl"
          class="image-preview"
        >
        <div class="image-actions">
          <v-btn
            icon
            small
            color="error"
            @click.stop="removeImage"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-overlay
        v-if="isUploading"
        absolute
        opacity="0.8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <div class="mt-2 white--text">
          正在上传中...
        </div>
      </v-overlay>

      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden-input"
        @change="onFileSelected"
      >
    </v-card>

    <div
      v-if="errorMessage"
      class="error-message text-caption text-error mt-1"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>

export default {
  name: 'EnhancedImageUploader',
  props: {
    value: {
      type: [File, String, Blob, Object],
      default: null
    },
    hint: {
      type: String,
      default: 'Supported formats: JPG, PNG, GIF'
    },
    maxSizeMB: {
      type: Number,
      default: 1
    },
    required: {
      type: Boolean,
      default: false
    },
    root: {
      type: String,
      default: '/'
    }
  },
  data () {
    return {
      previewUrl: null,
      isDragging: false,
      errorMessage: null,
      isUploading: false,
      // Store the last compressed file for compatibility with both old and new implementations
      lastCompressedFile: null
    }
  },
  mounted () {
    // Ensure isUploading is false when component is mounted
    this.isUploading = false

    // Initialize previewUrl if value is a string
    this.updatePreviewFromValue()
  },
  watch: {
    value: {
      handler () {
        this.updatePreviewFromValue()
      },
      immediate: true
    }
  },
  methods: {
    updatePreviewFromValue () {
      console.log('updatePreviewFromValue', this.value)
      if (typeof this.value === 'string' && this.value) {
        // Check if the value is a complete URL
        const isCompleteUrl = /^(https?:\/\/|data:image)/.test(this.value)

        // If it's a complete URL, use it directly; otherwise, prepend the root
        this.previewUrl = isCompleteUrl ? this.value : `${this.root}${this.value.startsWith('/') ? this.value.substring(1) : this.value}`
      } else if (this.value instanceof Blob || this.value instanceof File) {
        // If value is a Blob or File, create an object URL
        this.previewUrl = URL.createObjectURL(this.value)
      } else if (!this.value || this.value === '') {
        // If value is null/undefined/empty string, reset previewUrl
        this.previewUrl = null
      }
    },
    triggerFileInput () {
      this.$refs.fileInput.click()
    },
    async onFileSelected (event) {
      const file = event.target.files[0]
      if (!file) return
      try {
        this.errorMessage = null
        this.isUploading = true
        this.previewUrl = URL.createObjectURL(file)
        this.$emit('input', file)
      } catch (error) {
        console.error('Error processing image:', error)
        this.errorMessage = 'Error processing image. Please try another file.'
      } finally {
        this.isUploading = false
      }

      // Reset the input so the same file can be selected again
      event.target.value = null
    },
    onDragOver (event) {
      this.isDragging = true
    },
    onDragLeave (event) {
      this.isDragging = false
    },
    async onDrop (event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (!file || !file.type.startsWith('image/')) {
        this.errorMessage = 'Please drop an image file'
        return
      }

      try {
        this.errorMessage = null
        this.isUploading = true

        this.previewUrl = URL.createObjectURL(file)

        this.$emit('input', file)
      } catch (error) {
        console.error('Error processing image:', error)
        this.errorMessage = 'Error processing image. Please try another file.'
      } finally {
        this.isUploading = false
      }
    },
    removeImage () {
      this.previewUrl = null
      this.lastCompressedFile = null

      // For compatibility with both old and new implementations:
      // If the original value was a string, emit an empty string
      // Otherwise, emit null
      if (typeof this.value === 'string') {
        this.$emit('input', '')
      } else {
        this.$emit('input', null)
      }
    }
  }
}
</script>

<style scoped>
.enhanced-image-uploader {
  width: 100%;
}

.upload-area {
  cursor: pointer;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ccc;
  transition: all 0.3s;
}

.upload-area:hover {
  border-color: #2196F3;
}

.upload-area.has-image {
  border-style: solid;
}

.upload-placeholder {
  text-align: center;
  padding: 20px;
  color: #757575;
}

.hidden-input {
  display: none;
}

.image-preview-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
}

.image-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
}

.error-message {
  color: #f44336;
}
</style>
